import {useSelector} from "react-redux";
import {useCallback, useMemo} from "react";
import {
    selectComment,
    selectCommentList,
    selectCommentThread,
    selectCommunities,
    selectCommunityListEntry,
    selectContent,
    selectContentList,
    selectFeedEntry,
    selectFeedIsLoaded,
    selectPost,
    selectPosts,
    selectProfile,
    selectSavedFeed,
    selectUsername
} from "./selectors";

export const useBaseCommunity = () => {
    return useSelector((state) => state.baseCommunity);
};

export const useLastFeedPos = () => {
    return useSelector((state) => state.lastFeedPos);
};

export const useGoogleAdsEnabled = () => {
    return useSelector((state) => state.googleAdsEnabled);
};

export const useIsFeedLoaded = (key) => {
    return useSelector(useCallback(selectFeedIsLoaded(key), [key]));
};

export const useStoreFeed = (key, withHidden=false, withDeleted=false) => {
    const { ids, more, loading, failures } = useSelector(selectFeedEntry(key)) || {ids: [], more: true, loading: false};
    const posts = useSelector(selectPosts(ids || [], withHidden, withDeleted));
    return { posts, more, loading, failures };
};

export const useStoreComment = (id) => {
    return useSelector(useCallback(selectComment(id), [id]));
};

export const useStoreCommentList = (key) => {
    return useSelector(selectCommentList(key));
};

export const useStoreCommentThread = (key) => {
    return useSelector(useCallback(selectCommentThread(key), [key]));
};

export const useStorePostComments = (id, commentSort) => {
    return useStoreCommentList(`p/${id}:${commentSort}`);
};

export const useStoreChildComments = (id) => {
    return useStoreCommentList(`c/${id}`);
};

export const useStorePost = (id, withComments) => {
    return useSelector(selectPost(id, withComments));
};

export const useStoreSavedFeed = (id) => {
    return useSelector(selectSavedFeed(id));
};

export const useStoreContentList = (key) => {
    const {items, more} = useSelector(selectContentList(key)) || { items: [], more: false };
    return { content: useSelector(selectContent(items)), more };
};

export const useStoreCommunityList = (sort) => {
    const entry = useSelector(selectCommunityListEntry(sort));
    return useSelector(selectCommunities(entry?.names));
}

export const useStoreCommunity = (name) => {
    const community = useSelector(({communityMap}) => communityMap.get(name?.toLowerCase()));
    const subscribed = useSelector((state) => selectProfile(state).subscribed || []);
    const moderates = useSelector((state) => selectProfile(state).moderates || []);
    return useMemo(() => ({
        ...community,
        is_moderator: moderates.includes(community?.name),
        is_subscribed: subscribed.includes(community?.name),
    }), [community, moderates, subscribed]);
};

export const useStoreCommunityTitle = (name, loggedIn, customFeed) => {
    const canonName = name?.toLowerCase() || 'win';
    const community = useStoreCommunity(canonName);
    if (canonName === 'all' || customFeed) return 'All';
    if (canonName === 'trending') return 'Trending';
    if (canonName === 'home' || canonName === 'win') return 'Home';
    return community?.name || name;
};

export const useUsername = () => useSelector(selectUsername);

export const useSlug = (text) => useMemo(() => {
    let slug = text.replace(/[^a-zA-Z0-9\s]/g, "").replace(/ /g, "-").toLowerCase();
    slug = slug.slice(0, 32);
    if (slug.length === 0) {
        slug = 'x';
    }
    return slug;
}, [text]);

export function usePostLink(post) {
   const baseCommunity = useSelector(({baseCommunity}) => baseCommunity);
    const slug = useSlug(post?.title || "");
   if (!post) {
       return "";
   }
   if (baseCommunity === 'win' || baseCommunity !== post.community) {
       return `/c/${post.community}/p/${post.uuid}/${slug}/c`;
   }
   return `/p/${post.uuid}/${slug}/c`;
}

export const useFeedAdUnits = (key) => {
    return useSelector((state) => {
        const selectedFeedItem = state.feedAdUnits[key];
        return selectedFeedItem ? selectedFeedItem : null;
    });
};
